import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ASSIGNABLE_ROLES, DEPARTMENT_OPTIONS } from '@/constants';
import { apiService } from '@/lib/axios';

const AddUserModal = ({ open, onOpenChange, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [department, setDepartment] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [selectedGroups, setSelectedGroups] = useState(['User']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setEmail('');
      setName('');
      setDesignation('');
      setDepartment('');
      setTemporaryPassword('');
      setSelectedGroups(['User']);
      setLoading(false);
      setError(null);
      setSuccess(false);
    }
    onOpenChange(isOpen);
  };

  const toggleGroup = (group) => {
    setSelectedGroups((prev) => {
      if (prev.includes(group)) {
        if (prev.length === 1) return prev;
        return prev.filter((g) => g !== group);
      }
      return [...prev, group];
    });
  };

  const isValid = email.trim() && name.trim() && designation.trim() && department && temporaryPassword.length >= 8;

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      await apiService.users.createCognitoUser({
        email: email.trim(),
        name: name.trim(),
        designation: designation.trim(),
        department: department.trim(),
        temporaryPassword,
        groups: selectedGroups,
      });
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>
            Create a new user in the system. They will need to change their
            password on first login.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-4">
            <p className="text-sm text-green-600">
              User created successfully. They can now log in with their
              temporary password.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium" htmlFor="add-user-email">
                Email
              </label>
              <Input
                id="add-user-email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm font-medium" htmlFor="add-user-name">
                Name
              </label>
              <Input
                id="add-user-name"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm font-medium" htmlFor="add-user-designation">
                Designation <span className="text-red-500">*</span>
              </label>
              <Input
                id="add-user-designation"
                type="text"
                placeholder="e.g. Quality Manager, Senior Engineer"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm font-medium" htmlFor="add-user-department">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                id="add-user-department"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                disabled={loading}
              >
                <option value="">Select department</option>
                {DEPARTMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium" htmlFor="add-user-password">
                Temporary Password
              </label>
              <Input
                id="add-user-password"
                type="password"
                placeholder="Min 8 characters"
                value={temporaryPassword}
                onChange={(e) => setTemporaryPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Roles</label>
              <div className="mt-2 space-y-2">
                {ASSIGNABLE_ROLES.map((group) => (
                  <label key={group} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group)}
                      onChange={() => toggleGroup(group)}
                      disabled={
                        loading ||
                        (selectedGroups.length === 1 &&
                          selectedGroups.includes(group))
                      }
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm">{group}</span>
                  </label>
                ))}
              </div>
              {selectedGroups.length === 1 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  At least one role must be selected.
                </p>
              )}
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        )}

        <DialogFooter>
          {success ? (
            <Button onClick={() => handleClose(false)}>Close</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={loading || !isValid}>
                {loading ? 'Creating...' : 'Create User'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;
